import React, { useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAnnouncementTypeDropdownQuery } from '../../../../api';

const AnnouncementTable = ({
  announcement,
  loading,
  filters = {},
  totalCount,
  pagination,
  onPageChange,
  handleEdit,
  handleDelete,
  onDownload
}) => {
  const { th } = useLanguage();
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);

  // Tüm announcement type'ları çekmek için API çağrıları
  const { data: salesTypes = [] } = useAnnouncementTypeDropdownQuery({ type: 128 });
  const { data: marketingTypes = [] } = useAnnouncementTypeDropdownQuery({ type: 129 });
  const { data: afterSalesTypes = [] } = useAnnouncementTypeDropdownQuery({ type: 130 });
  const { data: warrantyTypes = [] } = useAnnouncementTypeDropdownQuery({ type: 131 });
  const { data: sparePartsTypes = [] } = useAnnouncementTypeDropdownQuery({ type: 132 });

  // Tüm type'ları birleştir
  const allAnnouncementTypes = useMemo(() => {
    return [...salesTypes, ...marketingTypes, ...afterSalesTypes, ...warrantyTypes, ...sparePartsTypes];
  }, [salesTypes, marketingTypes, afterSalesTypes, warrantyTypes, sparePartsTypes]);

  // === Tür listeleri (sabitleyelim) ===
  const TYPE_OPTIONS = [
    { label: th('announcement.typeLabels.sales'), value: 128 },
    { label: th('announcement.typeLabels.marketing'), value: 129 },
    { label: th('announcement.typeLabels.afterSales'), value: 130 },
    { label: th('announcement.typeLabels.warranty'), value: 131 },
    { label: th('announcement.typeLabels.spareParts'), value: 132 }
  ];

  const ANNOUNCEMENT_TYPE_BY_TYPE = {
    128: [{ label: th('announcement.subTypeLabels.subsales'), value: 1 }],
    129: [{ label: th('announcement.subTypeLabels.updates'), value: 2 }],
    130: [
      { label: th('announcement.subTypeLabels.bulletin'), value: 3 },
      { label: th('announcement.subTypeLabels.warrantyOperations'), value: 4 }
    ],
    131: [{ label: th('announcement.subTypeLabels.bulletin'), value: 3 }],
    132: [
      { label: th('announcement.subTypeLabels.priceChanging'), value: 5 },
      { label: th('announcement.subTypeLabels.sparePartBulletin'), value: 6 },
      { label: th('announcement.subTypeLabels.labelOfGlassPalet'), value: 16 },
      { label: th('announcement.subTypeLabels.information'), value: 17 },
      { label: th('announcement.subTypeLabels.updatedPartNumber'), value: 18 }
    ]
  };

  const normalizedData = useMemo(() => {
    const raw =
      announcement?.data?.items ??
      announcement?.items ??
      announcement?.data ??
      announcement ??
      [];

    const arr = Array.isArray(raw) ? raw : [];
    return arr.map((item) => {
      // Type name'i çevir
      const typeName = item.typeName ?? TYPE_OPTIONS.find((t) => t.value === item.type)?.label ?? '';
      
      // Announcement type name'i API'den gelen dile göre çevrilmiş veriyle eşleştir
      const announcementTypeObj = allAnnouncementTypes.find(
        (at) => at.id === item.announcementType
      );
      const announcementTypeName = announcementTypeObj?.announcementType ?? item.announcementTypeName ?? '';

      return {
        ...item,
        typeName,
        announcementTypeName
      };
    });
  }, [announcement, th, allAnnouncementTypes, TYPE_OPTIONS]);

  const sortedData = useMemo(() => {
    if (!sortField || sortOrder === 0) return normalizedData;
    return [...normalizedData].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return sortOrder;
      if (bv == null) return -sortOrder;
      if (typeof av === 'string' && typeof bv === 'string')
        return sortOrder * av.localeCompare(bv);
      if (av < bv) return -sortOrder;
      if (av > bv) return sortOrder;
      return 0;
    });
  }, [normalizedData, sortField, sortOrder]);

  const confirmDelete = (row) =>
    confirmDialog({
      message: th('announcement.deleteConfirmMessage'),
      header: th('announcement.deleteConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: th('common.yes'),
      rejectLabel: th('common.no'),
      accept: () => handleDelete(row.id),
      rejectClassName: 'p-button-outlined'
    });

  const statusBodyTemplate = (row) => {
    const map = {
      1: { label: th('announcement.statusLabels.published'), cls: 'success' },
      2: { label: th('announcement.statusLabels.draft'), cls: 'warning' },
      0: { label: th('announcement.statusLabels.unpublished'), cls: 'danger' }
    };
    const { label, cls } = map[Number(row.status)] ?? {
      label: th('announcement.unknown'),
      cls: 'secondary'
    };
    return <span className={`p-badge p-badge-${cls}`}>{label}</span>;
  };

  const actionBodyTemplate = (row) => (
    <div className="flex items-center gap-3">
      <i
        onClick={() => handleEdit(row)}
        className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700"
        title={th('common.edit')}
      />
      <i
        onClick={() => confirmDelete(row)}
        className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700"
        title={th('common.delete')}
      />
    </div>
  );

  const downloadBodyTemplate = (row) => (
    <Button
      icon={
        downloadingId === row.id ? 'pi pi-spin pi-spinner' : 'pi pi-download'
      }
      className="p-button-text p-button-sm"
      tooltip={th('common.download')}
      aria-label={th('common.download')}
      onClick={async () => {
        try {
          setDownloadingId(row.id);
          await onDownload?.(row);
        } finally {
          setDownloadingId(null);
        }
      }}
    />
  );
  const typeBodyTemplate = (row) => {
  const typeOption = TYPE_OPTIONS.find(t => t.value === Number(row.type));
  return typeOption?.label || row.type;
};

  return (
    <>
      <ConfirmDialog />
      <DataTable
        value={sortedData}
        dataKey="id"
        lazy
        paginator
        first={(pagination?.PageNumber - 1) * pagination?.PageSize || 0}
        rows={pagination?.PageSize || 10}
        totalRecords={announcement?.data?.totalCount ?? totalCount ?? 0}
        onPage={onPageChange}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={loading}
        filters={filters}
        filterDisplay="menu"
        showGridlines
        stripedRows
        sortMode="single"
        emptyMessage={th('announcement.emptyMessage')}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th('table.paginatorTemplate')}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
        tableStyle={{ minWidth: '50rem' }}
      >
        <Column
          body={actionBodyTemplate}
          exportable={false}
          style={{ width: '5%', minWidth: '53px' }}
        />
        <Column
          sortable
          field="type"
          header={th('announcement.columnHeaders.type')}
           body={typeBodyTemplate}
          style={{ width: '15%', minWidth: '250px' }}
        />
        <Column
          sortable
          field="announcementTypeName"
          header={th('announcement.columnHeaders.announcementType')}
          style={{ width: '15%', minWidth: '180px' }}
          body={(r) => createTruncatedTextForTable(r.announcementTypeName, 40)}
        />
        <Column
          sortable
          field="brand"
          header={th('announcement.columnHeaders.brand')}
          style={{ width: '15%', minWidth: '120px' }}
        />
        <Column
          sortable
          field="announcementDate"
          header={th('announcement.columnHeaders.announcementDate')}
          body={(r) => dateOnlyBodyTemplate(r, 'announcementDate')}
          dataType="date"
          style={{ width: '10%' }}
        />
        <Column
          sortable
          field="announcementSubject"
          header={th('announcement.columnHeaders.subject')}
          body={(r) => createTruncatedTextForTable(r.announcementSubject, 40)}
        />
        <Column
          sortable
          field="announcementContent"
          header={th('announcement.columnHeaders.content')}
          body={(r) => createTruncatedTextForTable(r.announcementContent, 40)}
        />
        <Column
          field="status"
          header={th('announcement.columnHeaders.status')}
          body={statusBodyTemplate}
          sortable
          style={{ width: '5%' }}
        />
        <Column
          header={th('common.download')}
          exportable={false}
          body={downloadBodyTemplate}
          style={{ width: '6rem', textAlign: 'center' }}
        />
      </DataTable>
    </>
  );
};

export default AnnouncementTable;